// Tiny Camera Controller for iPad Safari / WebKit
// Usage:
//   const cam = createCameraController();
//   await cam.start({ videoEl });
//   cam.toggleFacing(); cam.setTorch(true); cam.setZoom(2.0);
//   await cam.stop();

type FacingMode = 'user' | 'environment';

export type CameraStartOpts = {
  videoEl: HTMLVideoElement;
  facing?: FacingMode;              // default: last used or 'environment'
  idealWidth?: number;              // default: 1920
  idealHeight?: number;             // default: 1080
  idealFps?: number;                // default: 30
  rememberPermissionKey?: string;   // default: 'ndg_camera_granted'
  rememberFacingKey?: string;       // default: 'ndg_facing_mode'
  autoplay?: boolean;               // default: true
  playsinline?: boolean;            // default: true
  mutedPreview?: boolean;           // default: true
};

export type CameraController = {
  start: (opts: CameraStartOpts) => Promise<MediaStream>;
  stop: () => Promise<void>;
  restart: (overrides?: Partial<CameraStartOpts>) => Promise<MediaStream>;
  toggleFacing: () => Promise<MediaStream>;
  setFacing: (facing: FacingMode) => Promise<MediaStream>;
  setTorch: (on: boolean) => Promise<boolean>;
  setZoom: (val: number) => Promise<number>; // returns clamped value
  getZoomRange: () => { min: number; max: number; step: number } | null;
  isActive: () => boolean;
  getStream: () => MediaStream | null;
  getFacing: () => FacingMode;
};

export function createCameraController(): CameraController {
  let stream: MediaStream | null = null;
  let videoElRef: HTMLVideoElement | null = null;
  let facing: FacingMode = (localStorage.getItem('ndg_facing_mode') as FacingMode) || 'environment';
  let optsCache: CameraStartOpts | null = null;

  const defaultOpts: Required<Omit<CameraStartOpts, 'videoEl' | 'facing'>> = {
    idealWidth: 1920,
    idealHeight: 1080,
    idealFps: 30,
    rememberPermissionKey: 'ndg_camera_granted',
    rememberFacingKey: 'ndg_facing_mode',
    autoplay: true,
    playsinline: true,
    mutedPreview: true,
  };

  function isStreamActive(s: MediaStream | null) {
    return !!(s && s.active && s.getVideoTracks().some(t => t.readyState === 'live'));
  }

  async function start(opts: CameraStartOpts): Promise<MediaStream> {
    const full: CameraStartOpts = { ...defaultOpts, ...opts };
    optsCache = { ...full };
    if (!full.facing) full.facing = facing;

    // If we already have an active stream and permission remembered, reuse it
    const remembered = localStorage.getItem(full.rememberPermissionKey || defaultOpts.rememberPermissionKey) === 'true';
    if (isStreamActive(stream) && remembered) {
      attach(videoElRef || full.videoEl);
      return stream!;
    }

    // Build constraints
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        facingMode: full.facing,
        width: { ideal: full.idealWidth, max: full.idealWidth },
        height: { ideal: full.idealHeight, max: full.idealHeight },
        frameRate: { ideal: full.idealFps, max: full.idealFps },
      }
    };

    // Request camera
    const newStream = await navigator.mediaDevices.getUserMedia(constraints);
    await bindStream(newStream, full);
    // Remember permission + facing on success
    try {
      localStorage.setItem(full.rememberPermissionKey!, 'true');
      localStorage.setItem(full.rememberFacingKey!, full.facing!);
    } catch { /* storage might be blocked; safe to ignore */ }

    return newStream;
  }

  async function bindStream(s: MediaStream, full: CameraStartOpts) {
    stream = s;
    videoElRef = full.videoEl;
    if (full.playsinline !== false) videoElRef!.setAttribute('playsinline', '');
    if (full.autoplay !== false) videoElRef!.setAttribute('autoplay', '');
    if (full.mutedPreview !== false) (videoElRef as any).muted = true; // Safari requires muted for autoplay
    attach(videoElRef!);
  }

  function attach(videoEl: HTMLVideoElement) {
    if (!stream) return;
    if (videoEl.srcObject !== stream) {
      videoEl.srcObject = stream;
      // iOS Safari sometimes needs a play() call after srcObject set
      videoEl.play?.().catch(() => {});
    }
  }

  async function stop() {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      stream = null;
    }
  }

  async function restart(overrides: Partial<CameraStartOpts> = {}) {
    const base = { ...(optsCache || {}), ...overrides };
    await stop();
    return start(base as CameraStartOpts);
  }

  async function setFacing(next: FacingMode) {
    facing = next;
    localStorage.setItem((optsCache?.rememberFacingKey || defaultOpts.rememberFacingKey), next);
    return restart({ facing: next });
  }

  async function toggleFacing() {
    return setFacing(facing === 'user' ? 'environment' : 'user');
  }

  function getVideoTrack(): MediaStreamTrack | null {
    if (!stream) return null;
    const vt = stream.getVideoTracks()[0];
    return vt || null;
    }

  // Torch support (some iOS devices w/ back camera)
  async function setTorch(on: boolean): Promise<boolean> {
    const track = getVideoTrack();
    if (!track) return false;
    // @ts-expect-error – advanced constraints not in TS lib for all browsers
    const caps = track.getCapabilities?.();
    // Torch only works on environment camera if supported
    if (!caps || !('torch' in caps)) return false;
    try {
      // @ts-expect-error – MediaTrackConstraints.advanced isn’t in TS lib for all browsers
      await track.applyConstraints({ advanced: [{ torch: !!on }] });
      return true;
    } catch {
      return false;
    }
  }

  // Zoom support: read capabilities, clamp value, apply
  function getZoomRange() {
    const track = getVideoTrack();
    const caps = track?.getCapabilities?.() as any;
    if (!caps || typeof caps.zoom !== 'number' && !caps.zoom) return null;
    // Some browsers return { min, max, step }; others only max
    const z = caps.zoom;
    if (typeof z === 'number') return { min: 1, max: z, step: 0.1 };
    return { min: z.min ?? 1, max: z.max ?? 1, step: z.step ?? 0.1 };
  }

  async function setZoom(val: number) {
    const track = getVideoTrack();
    if (!track) return 1;
    const range = getZoomRange();
    if (!range) return 1;
    const clamped = Math.max(range.min, Math.min(range.max, val));
    try {
      // @ts-expect-error – MediaTrackConstraints.advanced isn’t in TS lib for all browsers
      await track.applyConstraints({ advanced: [{ zoom: clamped }] });
    } catch { /* ignore */ }
    return clamped;
  }

  return {
    start,
    stop,
    restart,
    toggleFacing,
    setFacing,
    setTorch,
    setZoom,
    getZoomRange,
    isActive: () => isStreamActive(stream),
    getStream: () => stream,
    getFacing: () => facing,
  };
}